import { useEffect, useState } from "react";
import { Header } from "@/components/campaign/Header";
import { Hero } from "@/components/campaign/Hero";
import { ReviewSection, InlineReview } from "@/components/campaign/Reviews";
import { Recognition, VisitDifference, MeetDoctor, Expectations } from "@/components/campaign/InfoSections";
import { PerimenopauseDeep } from "@/components/campaign/PerimenopauseDeep";
import { InstagramSection } from "@/components/campaign/InstagramSection";
import { PricingSection } from "@/components/campaign/PricingSection";
import { HowItWorks } from "@/components/campaign/HowItWorks";
import { FAQSection } from "@/components/campaign/FAQSection";
import { FinalCTA } from "@/components/campaign/FinalCTA";
import { Footer } from "@/components/campaign/Footer";
import { StickyCTA } from "@/components/campaign/StickyCTA";
import { Quiz } from "@/components/campaign/Quiz";
import { ContactFormDialog } from "@/components/campaign/ContactFormDialog";
import { GuideDialog } from "@/components/campaign/GuideDialog";
import { BookingDialog } from "@/components/campaign/BookingDialog";
import { BOOKING_CHOICE, BOOKING_LINKS } from "@/lib/campaign";
import { track } from "@/lib/track";
import { Seo } from "@/components/Seo";
import { HsaCallout } from "@/components/campaign/HsaCallout";

const SEO = {
  menopause: {
    title: "Menopause & Perimenopause Care in Easton, PA | Dr. Jill Crosson",
    description:
      "Menopause and perimenopause care in Easton, PA with Dr. Jill Crosson, DO. Spend a full hour reviewing your health history and next steps.",
    path: "/menopause",
  },
  "menopause-call": {
    title: "Talk With a Menopause Doctor in Easton, PA | Dr. Jill Crosson",
    description:
      "Call (484) 544-3345 to talk with Dr. Jill Crosson's office about menopause and perimenopause care in Easton, PA. Unhurried, physician-led visits.",
    path: "/menopause-call",
  },
};

export default function Landing({ variant }) {
  const guided = variant === "menopause";
  const [quizOpen, setQuizOpen] = useState(false);
  const [contact, setContact] = useState({ open: false, prefill: null });
  const [guideOpen, setGuideOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [hsaOpen, setHsaOpen] = useState(false);

  useEffect(() => {
    if (!guided || sessionStorage.getItem("hsaCalloutShown")) return undefined;
    const t = setTimeout(() => {
      if (document.querySelector('[data-testid="quiz-overlay"]')) return;
      sessionStorage.setItem("hsaCalloutShown", "1");
      setHsaOpen(true);
    }, 7000);
    return () => clearTimeout(t);
  }, [guided]);

  const openBooking = () => {
    track(variant, "booking_click");
    setBookOpen(true);
  };

  useEffect(() => {
    track(variant, "page_view");
    window.scrollTo(0, 0);
  }, [variant]);

  useEffect(() => {
    if (sessionStorage.getItem("guidePopupShown")) return undefined;
    let maxScroll = 0;
    const trigger = () => {
      if (sessionStorage.getItem("guidePopupShown")) return;
      if (document.querySelector('[data-testid="quiz-overlay"]')) return;
      sessionStorage.setItem("guidePopupShown", "1");
      track(variant, "guide_open");
      setGuideOpen(true);
      remove();
    };
    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) trigger();
    };
    const onScroll = () => {
      const y = window.scrollY;
      maxScroll = Math.max(maxScroll, y);
      if (maxScroll > window.innerHeight * 1.5 && y < maxScroll - window.innerHeight * 1.2 && y < 400) {
        trigger();
      }
    };
    const remove = () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };
    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    return remove;
  }, [variant]);

  const openGuide = () => {
    sessionStorage.setItem("guidePopupShown", "1");
    track(variant, "guide_open");
    setGuideOpen(true);
  };

  const startGuide = () => {
    track(variant, "cta_click");
    track(variant, "quiz_start");
    setQuizOpen(true);
  };

  const askQuestion = (prefill) => setContact({ open: true, prefill });

  return (
    <div id="top" className="min-h-screen pb-20 md:pb-0 text-left">
      <Seo {...SEO[variant]} />
      <Header variant={variant} onBook={openBooking} />
      <main>
        <Hero
          variant={variant}
          onStartGuide={startGuide}
          onHsaInfo={guided ? () => setHsaOpen(true) : undefined}
        />
        <ReviewSection />
        <Recognition />
        <PerimenopauseDeep onStartGuide={startGuide} onDownloadGuide={openGuide} />
        <VisitDifference />
        {!guided && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
            <InlineReview
              testId="review-jane-domitrowits"
              quote="My visit with Dr. Crosson was a very positive experience because she took the time to listen and make sure my questions were answered in a way I could understand."
              name="Jane Domitrowits"
              sourceLabel="Verified Google Review"
              sidePhoto="/images/reviews/jane-domitrowits.webp"
              sidePhotoAlt="Jane Domitrowits with family"
              sidePhotoPosition="50% 25%"
            />
          </div>
        )}
        <MeetDoctor />
        <InstagramSection variant={variant} onStartGuide={startGuide} />
        <Expectations />
        {guided && <PricingSection onContact={askQuestion} />}
        <HowItWorks variant={variant} />
        <FAQSection />
        <FinalCTA variant={variant} onStartGuide={startGuide} />
      </main>
      <Footer variant={variant} />
      <StickyCTA variant={variant} onPrimary={startGuide} hidden={quizOpen} />
      {quizOpen && (
        <Quiz variant={variant} onClose={() => setQuizOpen(false)} onAskQuestion={askQuestion} />
      )}
      <ContactFormDialog
        open={contact.open}
        onOpenChange={(open) => setContact((c) => ({ ...c, open }))}
        prefill={contact.prefill}
        variant={variant}
      />
      <GuideDialog open={guideOpen} onOpenChange={setGuideOpen} variant={variant} />
      {guided && <HsaCallout open={hsaOpen && !quizOpen} onClose={() => setHsaOpen(false)} />}
      <BookingDialog
        open={bookOpen}
        onClose={() => setBookOpen(false)}
        choiceLabel={BOOKING_CHOICE.hormone}
        variant={variant}
      />
    </div>
  );
}
